/* ShelterDetail — TR-style public shelter page.
   Route: /app/rifugio/:sid
   - Hero cover
   - About + info (city, contacts, website)
   - Cani in adozione (grid TR cards)
   - Missioni attive
   - CTA "Contatta il rifugio" → ShelterInquireModal → chat thread
   Attention to mobile navbar: bottom padding safe area 128px.
*/
import React, { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft, MapPin, Phone, Mail, Globe, CheckCircle2, PawPrint,
  MessageCircle, X, Send, Loader2, Sparkles, HeartHandshake, Info,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatErr } from "@/lib/api";
import { hapticTap } from "@/lib/haptics";
import { orioIllustration, orioIllustrationFallback } from "@/lib/illustrations";

const ADOPTION_STATUS_LABEL = {
  adoptable: { label: "Adozione", color: "#2DBA60" },
  fostering: { label: "In stallo", color: "#FF7A1A" },
  reserved:  { label: "Riservato", color: "#F59E0B" },
  adopted:   { label: "Adottato", color: "#9AA0A6" },
};

export default function ShelterDetail() {
  const { sid } = useParams();
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [missions, setMissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inquiring, setInquiring] = useState(false);
  const [selectedDog, setSelectedDog] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get(`/shelter/${sid}`);
      setData(d);
      const m = await api.get("/shelter/missions/active");
      setMissions((m.data.items || []).filter((mi) => mi.shelter_id === sid));
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Rifugio non trovato");
      nav("/app");
    } finally {
      setLoading(false);
    }
  }, [sid, nav]);

  useEffect(() => { load(); }, [load]);

  if (loading || !data) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-[#FF7A1A]" />
      </div>
    );
  }

  const s = data.shelter;
  const dogs = data.dogs || [];
  const coverSrc = s.cover_photo_url || orioIllustration("shelter-hero");

  return (
    <div className="pb-[132px] md:pb-8" data-testid="shelter-detail-page">
      {/* Hero */}
      <div className="relative w-full" style={{ aspectRatio: "16/9", maxHeight: "42vh" }}>
        <img
          src={coverSrc}
          onError={(e) => { e.currentTarget.src = orioIllustrationFallback("shelter-hero"); }}
          alt={s.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0"
          style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0) 40%, rgba(251,247,241,0.9) 100%)" }} />
        <button
          onClick={() => nav(-1)}
          data-testid="shelter-back"
          className="absolute top-4 left-4 h-10 w-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center border border-[#F0EBE3]"
          aria-label="Indietro"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="absolute top-4 right-4 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] text-white bg-[#2DBA60]"
          style={{ boxShadow: "0 6px 16px -4px rgba(45,186,96,0.55)" }}>
          <CheckCircle2 className="h-3 w-3" /> Verificato ORIO
        </div>
      </div>

      {/* Header + CTA */}
      <div className="max-w-3xl mx-auto px-4 sm:px-6 -mt-8 relative z-10">
        <div className="bg-white rounded-3xl border border-[#F0EBE3] p-5 shadow-[0_20px_40px_-24px_rgba(0,0,0,0.12)]">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] font-semibold inline-flex items-center gap-1.5">
                <HeartHandshake className="h-3 w-3" /> Rifugio ORIO
              </div>
              <h1 className="mt-1 font-display text-2xl sm:text-3xl leading-tight text-[#1A1A1A]">
                {s.name}
              </h1>
              <div className="mt-1 text-[13px] text-[#4A4A4A] inline-flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" />
                {s.city}{s.address ? ` · ${s.address}` : ""}
              </div>
            </div>
            <button
              onClick={() => { hapticTap(); setInquiring(true); }}
              data-testid="shelter-contact-cta"
              className="shrink-0 h-11 px-4 rounded-full font-bold text-[13.5px] text-white inline-flex items-center gap-1.5"
              style={{
                background: "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
                boxShadow: "0 12px 24px -8px rgba(255,122,26,0.55)",
              }}
            >
              <MessageCircle className="h-4 w-4" /> Contatta
            </button>
          </div>

          {/* Info tags */}
          <div className="mt-3 flex flex-wrap gap-2">
            {s.phone && <InfoChip icon={Phone} v={s.phone} href={`tel:${s.phone}`} />}
            {s.email && <InfoChip icon={Mail}  v={s.email} href={`mailto:${s.email}`} />}
            {s.website && <InfoChip icon={Globe} v={s.website.replace(/^https?:\/\//, "")} href={s.website} />}
          </div>

          {/* About */}
          <div className="mt-4 pt-4 border-t border-[#F0EBE3]">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#9A9A9A] font-semibold mb-1.5">Chi siamo</div>
            <p className="text-[13.5px] text-[#4A4A4A] leading-relaxed whitespace-pre-wrap">{s.description}</p>
          </div>
        </div>

        {/* Cani in adozione */}
        <section className="mt-6" data-testid="shelter-dogs-section">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#9A9A9A] font-semibold">
                {dogs.length > 0 ? `${dogs.length} amici in cerca di casa` : "Nessun cane pubblicato"}
              </div>
              <h2 className="font-display text-xl leading-tight">Cani in adozione</h2>
            </div>
          </div>

          {dogs.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-[#E5DECF] p-6 text-center text-[13px] text-[#7A7A7A]">
              Questo rifugio non ha ancora schede cani. Torna presto!
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {dogs.map((d) => (
                <DogCard key={d.id} d={d}
                  onContact={() => { setSelectedDog(d); setInquiring(true); }} />
              ))}
            </div>
          )}
        </section>

        {/* Missioni */}
        {missions.length > 0 && (
          <section className="mt-6" data-testid="shelter-missions-section">
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#9A9A9A] font-semibold mb-1">Aiutali oggi</div>
            <h2 className="font-display text-xl leading-tight mb-3">Richieste della community</h2>
            <div className="space-y-2">
              {missions.map((m) => <MissionCard key={m.id} m={m} />)}
            </div>
          </section>
        )}

        {/* Sicurezza / Trust */}
        <div className="mt-6 rounded-2xl bg-[#E8F4EA] border border-[#A8D3B4] p-4 flex items-start gap-3">
          <div className="h-8 w-8 rounded-lg bg-[#2DBA60] text-white flex items-center justify-center shrink-0">
            <CheckCircle2 className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[13px] font-semibold text-[#1A5A2E]">Rifugio verificato manualmente</div>
            <div className="text-[12px] text-[#3A5A45] leading-relaxed">
              L&apos;identità e i documenti di questo rifugio sono stati controllati dal team ORIO prima dell&apos;attivazione.
            </div>
          </div>
        </div>
      </div>

      {inquiring && (
        <ShelterInquireModal
          shelter={s}
          dog={selectedDog}
          onClose={() => { setInquiring(false); setSelectedDog(null); }}
        />
      )}
    </div>
  );
}

function InfoChip({ icon: Icon, v, href }) {
  const inner = (
    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11.5px] font-semibold bg-[#FBF7EF] border border-[#F0EBE3] text-[#4A4A4A]">
      <Icon className="h-3 w-3 text-[#FF7A1A]" /> {v}
    </span>
  );
  return href ? <a href={href} target="_blank" rel="noreferrer">{inner}</a> : inner;
}

function DogCard({ d, onContact }) {
  const status = ADOPTION_STATUS_LABEL[d.adoption_status] || ADOPTION_STATUS_LABEL.adoptable;
  return (
    <button
      data-testid={`shelter-dog-${d.id}`}
      onClick={onContact}
      className="text-left rounded-2xl bg-white border border-[#F0EBE3] overflow-hidden hover:shadow-lg transition"
    >
      <div className="relative w-full" style={{ aspectRatio: "1/1", background: "#F5EFE6" }}>
        {d.photo_url ? (
          <img src={d.photo_url} alt={d.name} className="w-full h-full object-cover" loading="lazy" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <PawPrint className="h-10 w-10 text-[#F4B83C]" />
          </div>
        )}
        <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full text-[9.5px] font-bold uppercase tracking-wide text-white"
          style={{ background: status.color }}>
          {status.label}
        </span>
      </div>
      <div className="p-2.5">
        <div className="font-semibold text-[13.5px] text-[#1A1A1A] truncate">{d.name}</div>
        <div className="text-[11px] text-[#7A7A7A] truncate">
          {d.breed || "Meticcio"}{d.sex ? ` · ${d.sex === "F" ? "♀" : "♂"}` : ""}
        </div>
      </div>
    </button>
  );
}

function MissionCard({ m }) {
  const icons = { food: "🥫", water: "💧", money: "💶", toys: "🎾", meds: "💊", blankets: "🛏️", other: "✨" };
  return (
    <div className="rounded-2xl bg-white border border-[#F0EBE3] p-3 flex items-center gap-3">
      <div className="h-10 w-10 rounded-xl flex items-center justify-center text-xl bg-[#FFF6E6]">
        {icons[m.kind] || "✨"}
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[13.5px] font-semibold truncate">{m.title}</div>
        {m.description && <div className="text-[11.5px] text-[#7A7A7A] truncate">{m.description}</div>}
      </div>
      {m.target_amount && (
        <div className="text-[11px] font-semibold text-[#2DBA60] shrink-0">
          {m.target_amount}{m.unit || ""}
        </div>
      )}
    </div>
  );
}

function ShelterInquireModal({ shelter, dog, onClose }) {
  const nav = useNavigate();
  const [msg, setMsg] = useState(
    dog
      ? `Ciao! Sono interessato/a ad adottare ${dog.name}. Potreste darmi qualche informazione in più?`
      : "Ciao! Vorrei avere qualche informazione sui vostri cani in adozione."
  );
  const [busy, setBusy] = useState(false);

  const send = async () => {
    if (msg.trim().length < 3) return toast.error("Scrivi un messaggio prima");
    setBusy(true);
    try {
      const { data } = await api.post(`/shelter/${shelter.id}/inquire`, {
        dog_id: dog?.id,
        subject: dog ? `Adozione ${dog.name}` : "Info generali",
        message: msg,
      });
      hapticTap();
      toast.success("Messaggio inviato! Il rifugio ti risponderà via chat.");
      onClose();
      nav(`/app/rifugio/chat/${data.thread_id}`);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "Errore invio");
    } finally { setBusy(false); }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }}
      className="fixed inset-0 z-[1300] bg-black/60 flex items-end justify-center"
      onClick={onClose}
    >
      <motion.div
        initial={{ y: "100%" }} animate={{ y: 0 }}
        transition={{ type: "spring", damping: 30, stiffness: 260 }}
        className="w-full max-w-lg bg-[#FBF7EF] rounded-t-[28px] p-5"
        style={{ paddingBottom: "calc(env(safe-area-inset-bottom, 0px) + 24px)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[10.5px] uppercase tracking-[0.22em] text-[#C25612] font-semibold inline-flex items-center gap-1.5">
              <MessageCircle className="h-3 w-3" /> Nuova conversazione
            </div>
            <div className="font-display text-xl mt-0.5">
              {dog ? `Adotta ${dog.name}` : `Contatta ${shelter.name}`}
            </div>
          </div>
          <button onClick={onClose} className="h-9 w-9 rounded-full bg-black/5 flex items-center justify-center">
            <X className="h-4 w-4" />
          </button>
        </div>

        {dog && (
          <div className="mb-3 flex items-center gap-3 p-2 rounded-xl bg-white border border-[#F0EBE3]">
            {dog.photo_url && (
              <img src={dog.photo_url} className="h-12 w-12 rounded-lg object-cover" alt={dog.name} />
            )}
            <div>
              <div className="font-semibold text-[13.5px]">{dog.name}</div>
              <div className="text-[11.5px] text-[#7A7A7A]">{dog.breed || "Meticcio"}</div>
            </div>
          </div>
        )}

        <textarea
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          rows={5}
          data-testid="shelter-inquire-text"
          className="w-full rounded-xl px-3 py-3 text-[14px] bg-white border border-[#F0EBE3] outline-none focus:border-[#FF7A1A] resize-none"
        />

        <div className="mt-2 text-[11.5px] text-[#7A7A7A] inline-flex items-start gap-1.5">
          <Info className="h-3 w-3 shrink-0 mt-0.5" />
          Il messaggio arriva nella chat del rifugio. La risposta ti arriva come notifica ORIO.
        </div>

        <button
          onClick={send}
          disabled={busy}
          data-testid="shelter-inquire-send"
          className="w-full mt-4 h-12 rounded-full font-bold text-white text-[14px] inline-flex items-center justify-center gap-2"
          style={{
            background: busy ? "#E5DECF" : "linear-gradient(135deg, #FF7A1A 0%, #F4B83C 100%)",
            color: busy ? "#9A9A9A" : "white",
            boxShadow: busy ? "none" : "0 12px 24px -8px rgba(255,122,26,0.55)",
          }}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
          {busy ? "Invio…" : "Invia messaggio"}
        </button>
      </motion.div>
    </motion.div>
  );
}
